import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Box, Button, Modal, TextField } from "@mui/material";
import Chat from "../components/chat";
import ChatbotNavbar from "../components/chatbot_navbar";
import ManageDocs from "../components/manage-docs";
import ManageHelpModal from "../components/manage-help-modal";
import PageLoader from "../components/page_loader";
import useChatbot from "../provider/chatbot_provider";
import { getClient } from "../utils/http";
import { decrypt, encrypt } from "../utils/encryption";
import SelectedPolicies from "../components/kyp/SelectPolicies";
import { toast } from "react-toastify";

export default function Chatbot({ tool }: { tool: string }) {
  const [open, setOpen] = useState(false);
  const [openHelpModal, setOpenHelpModal] = useState(false);
  const [openKypModal, setKypModal] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [kypMessage, setKypMessage] = useState("");
  const [vectorName, setVectorName] = useState("");
  const [loader, setLoader] = useState(false);
  const location = useLocation();
  const [mobile, setMobile] = useState("");
  const {
    setActivePolicies,
    newChat,
    chats,
    activePolicies,
    isDocsProcessing,
  } = useChatbot();
  const [pageLoader, setPageLoader] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow only digits and limit to 10 characters
    if (/^\d{0,10}$/.test(value)) {
      setMobile(value);
    }
  };

  const handleOpen = (pathname: string) => {
    if (
      [
        "/document",
        "/legal",
        "/qc",
        "/wc-claims",
        "/tech-eo",
        "/tech-do",
        "/dtr",
      ].includes(pathname)
    ) {
      if (isDocsProcessing) {
        toast.info("Files received. Processing may take a few minutes...", {
          autoClose: 5000,
        });
        return;
      }
      setOpen(true);
    } else if (pathname === "/kyp") setKypModal(true);
    else setOpenHelpModal(true);
  };
  const handleClose = () => {
    setOpen(false);
    setOpenHelpModal(false);
    setKypModal(false);
  };

  const handleDrawer = useCallback((action: boolean) => {
    setIsDrawerOpen(action);
  }, []);

  const onClickMobileSubmit = () => {
    setKypMessage("");
    localStorage.setItem("mobile", mobile);
    setLoader(true);
    getClient("kyp")
      .post("/active-policies", {
        // data: encrypt(JSON.stringify({ mobileNo: mobile })),
        data: { mobileNo: mobile },
        encryption: true,
      })
      .then((data) => {
        setLoader(false);
        // const res = JSON.parse(decrypt(data.data.data));
        const res = data.data.data;
        const hasActivePolicies = res?.result.includes("Active Policies");
        if (!hasActivePolicies) {
          setKypMessage(res?.result);
        } else {
          const input = res?.result;
          const policiesPart = input.split("Active Policies:")[1];
          const activePolicies = policiesPart
            .split(",")
            .map((p: any) => p.trim());
          const transformData = activePolicies.map((policy: string) => {
            return {
              policies: policy,
              isSelected: false,
            };
          });
          setActivePolicies(transformData || null);
        }
      })
      .catch((err) => {
        setLoader(false);
        console.log(err);
      });
  };

  const handleKypclose = () => {
    setPageLoader(true);
    const selectedPolicy = (
      activePolicies ? activePolicies : [{ policies: "" }]
    ).find((p: any) => p.isSelected)?.policies;
    getClient("kyp")
      .post("/initiate-policy-rag", {
        // data: encrypt(JSON.stringify({ policyNumber: selectedPolicy })),
        data: { policyNumber: selectedPolicy },
        encryption: true,
      })
      .then((data) => {
        setPageLoader(false);
        setKypModal(false);
      })
      .catch((err) => {
        localStorage.removeItem("mobile");
        localStorage.removeItem("selectedPolicies");
        setActivePolicies(null);
        setKypModal(true);
        setPageLoader(false);
        console.log(err);
      });
  };

  useEffect(() => {
    let pathname = location.pathname;
    if (
      [
        "/document",
        "/help",
        "/legal",
        "/qc",
        "/kyp",
        "/wc-claims",
        "/tech-eo",
        "/tech-do",
        "/dtr",
      ].includes(pathname)
    ) {
      handleOpen(pathname);
    } else {
      handleClose();
    }
  }, [location.pathname, location.search]);
  useEffect(() => {
    const open = chats[chats?.length - 1]?.conversations?.length;
    if (location.pathname === "/kyp" && !openKypModal && !open) {
      setKypModal(true);
    }
  }, [newChat, chats]);
  return (
    <div className="App">
      <Box display="flex" height="100svh">
        <ChatbotNavbar
          tool={tool}
          vectorName={vectorName}
          handleOpen={handleOpen}
          isDrawerOpen={isDrawerOpen}
          handleDrawer={handleDrawer}
        />
        <Box
          flex={1}
          bgcolor={"#f6f9f8"}
          sx={{
            background:
              "linear-gradient(260deg, #ff817f 0%, #fed0b2 0%, #ffffff 25%)",
          }}
        >
          <Chat
            vectorName={vectorName}
            tool={tool}
            isDrawerOpen={isDrawerOpen}
            handleDrawer={handleDrawer}
            handleOpen={() => handleOpen(location.pathname)}
          />
        </Box>

        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box
            sx={{
              position: "absolute" as "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "auto",
              maxWidth: "95vw",
              maxHeight: "70vh",
              bgcolor: "background.paper",
              borderRadius: 1,
              boxShadow: 24,
              p: 2.5,
              pb: 2,
              overflow: "hidden",
              overflowY: "auto",
              display: "block",
            }}
          >
            <ManageDocs
              vectorName={vectorName}
              setVectorName={setVectorName}
              handleClose={handleClose}
            />
          </Box>
        </Modal>

        <Modal
          open={openHelpModal}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box
            sx={{
              position: "absolute" as "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "auto",
              maxWidth: "95vw",
              maxHeight: "70vh",
              bgcolor: "background.paper",
              borderRadius: 1,
              boxShadow: 24,
              p: 2.5,
              pb: 2,
              overflow: "hidden",
              overflowY: "auto",
              display: "block",
            }}
          >
            <ManageHelpModal handleClose={handleClose} />
          </Box>
        </Modal>

        <Modal
          open={openKypModal}
          onClose={setKypModal}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box
            sx={{
              position: "absolute" as "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "30vw",
              maxWidth: "95vw",
              maxHeight: "70vh",
              height: "auto",
              bgcolor: "background.paper",
              borderRadius: 1,
              boxShadow: 24,
              p: 2.5,
              pb: 2,
              overflow: "hidden",
              overflowY: "auto",
              display: "block",
            }}
          >
            {!loader &&
              !localStorage.getItem("selectedPolicies") &&
              !activePolicies ? (
              <>
                <TextField
                  label="Mobile Number"
                  variant="outlined"
                  value={mobile}
                  onChange={handleChange}
                  inputProps={{
                    maxLength: 10,
                    inputMode: "numeric",
                    pattern: "[0-9]{10}",
                  }}
                  helperText={
                    mobile.length < 10
                      ? "Enter 10-digit mobile number"
                      : kypMessage
                        ? kypMessage
                        : ""
                  }
                  error={
                    (mobile.length > 0 && mobile.length < 10) || kypMessage
                      ? true
                      : false
                  }
                  fullWidth
                />
                <Box sx={{ textAlign: "center", width: "100%", mt: 2 }}>
                  <Button
                    sx={{ border: "1px solid gray" }}
                    onClick={onClickMobileSubmit}
                  >
                    Submit
                  </Button>
                </Box>
              </>
            ) : !localStorage.getItem("selectedPolicies") && !activePolicies ? (
              <PageLoader height="20vh" />
            ) : !pageLoader ? (
              <SelectedPolicies onClick={() => handleKypclose()} />
            ) : (
              pageLoader && <PageLoader height="20vh" />
            )}
          </Box>
        </Modal>
      </Box>
    </div>
  );
}
